/* eslint-disable react-hooks/exhaustive-deps */
"use client";

import { useUser } from "@auth0/nextjs-auth0/client";
import { Button, Text, Title } from "@mantine/core";
import { KeyboardControls, useProgress } from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import dynamic from "next/dynamic";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import React, { Suspense, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import EndScreen from "@/components/escaperoom/general/end-screen";
import InspectItem from "@/components/escaperoom/general/inspect-item";
import Interface from "@/components/escaperoom/general/interface";
import Lights from "@/components/escaperoom/general/lights";
import Menu from "@/components/escaperoom/general/menu";
import Sounds from "@/components/escaperoom/general/sounds";
import { Section } from "@/components/general/section";

import { useStateContext } from "../../../../context/context-provider";
import { useSocketContext } from "../../../../context/socket-context-provider";
import { useUserContext } from "../../../../context/user-context-provider";

const roomComponents = {
    buro: dynamic(() => import("@/components/escaperoom/buro/buro"), { ssr: false }),
    bar: dynamic(() => import("@/components/escaperoom/bar/bar"), { ssr: false }),
    island: dynamic(() => import("@/components/escaperoom/enchanted-island/enchanted-island"), {
        ssr: false,
    }),
    // kitchen: dynamic(() => import("@/components/escaperoom/kitchen/kitchen"), { ssr: false }),
    witchhood: dynamic(() => import("@/components/escaperoom/witchhood/witchhood"), { ssr: false }),
};

const roomSolvedStates = {
    buro: {
        deskKeyLock: { isSolved: false },
        deskNumberLock: { isSolved: false },
        chest: { isSolved: false },
        door: { isSolved: false },
        tresor: { isSolved: false },
    },
    bar: {
        darts: { isSolved: false },
        jukebox: { isSolved: false },
        slots: { isSolved: false },
        pooltable: { isSolved: false },
        disco: { isSolved: false },
    },
    island: {
        arrows: { isSolved: false },
        trees: { isSolved: false },
        obelisk: { isSolved: false },
        caveDoor: { isSolved: false },
        dominos: { isSolved: false },
        numberCircles: { isSolved: false },
        portal: { isSolved: false },
    },
    witchhood: {
        chest: { isSolved: false },
        furtuneTellingBall: { isSolved: false },
        book: { isSolved: false },
        kettle: { isSolved: false },
    },
    kitchen: {
        storage: { isSolved: false },
        pipes: { isSolved: false },
        pots: { isSolved: false },
        wine: { isSolved: false },
        spices: { isSolved: false },
    },
};

const EscapeRoom = () => {
    const params = useParams();
    const router = useRouter();
    const searchParams = useSearchParams();
    const lobbyParam = searchParams.get("lobby");

    const { t } = useTranslation();
    const escapeRoomsData = t("escaperooms", { returnObjects: true });
    const room = escapeRoomsData.find((r) => r.url === params.url);
    const EscapeRoomComponent = roomComponents[room?.url];

    const { lobbyId, joinLobby, lobbyUsers, socket, setJoined } = useSocketContext();
    const { user } = useUserContext();
    const { user: session } = useUser();
    const { setSolved, setEnvironment, setRoomUrl, inPointerLock, setInPointerLock, setMenu, setItems, inspectedItem } = useStateContext();

    const [triggered, setTriggered] = useState(false);
    const [newName, setNewName] = useState("");
    const [newColor, setNewColor] = useState("");

    const { progress } = useProgress();
    const sessionIdRef = useRef(null);
    const hasStartedRef = useRef(false);

    const getRandomColor = () =>
        "#" +
        Math.floor(Math.random() * 16_777_215)
            .toString(16)
            .padStart(6, "0");

    // Initialize room states
    useEffect(() => {
        if (room?.url) {
            setRoomUrl(room.url);
            setSolved(roomSolvedStates[room.url] || {});
            setItems({});
        }

        const handleContextMenu = (e) => e.preventDefault();
        document.addEventListener("contextmenu", handleContextMenu);

        return () => {
            document.removeEventListener("contextmenu", handleContextMenu);
        };
    }, [room?.url]);

    // Load player data
    useEffect(() => {
        setNewName(localStorage?.getItem("playerName") || (session ? session.name : "Gast"));
        let color = localStorage?.getItem("playerColor");
        if (!color) {
            color = getRandomColor();
            localStorage.setItem("playerColor", color);
        }
        setNewColor(color);
    }, [session]);

    useEffect(() => {
        if (lobbyParam && socket && !lobbyId) {
            const myInfo = { name: newName, color: newColor };

            if (lobbyUsers?.some((u) => u.id === socket.id)) {
                return;
            }

            joinLobby(lobbyParam, myInfo, (result) => {
                if ("error" in result) {
                    router.push("/");
                } else {
                    setJoined(true);
                }
            });
        }
    }, [lobbyParam, socket, lobbyId, newName, newColor, joinLobby, router, setJoined, lobbyUsers]);

    useEffect(() => {
        if (triggered || !room || progress < 100) {
            return;
        }
        setTriggered(true);

        if (lobbyId && socket) {
            socket.emit("startRoom", { lobbyId });
        } else {
            const defaultState = roomSolvedStates[room.url] || {};
            const storageData = JSON.parse(localStorage.getItem(room.url) || "{}");
            const storedSolved = storageData.solved || {};
            const mergedSolved = { ...defaultState };

            for (const key of Object.keys(storedSolved)) {
                mergedSolved[key] = {
                    ...defaultState[key],
                    ...storedSolved[key],
                    subTasks: {
                        ...defaultState[key]?.subTasks,
                        ...storedSolved[key]?.subTasks,
                    },
                };
            }

            setSolved(mergedSolved);

            if (storageData.environment) {
                setEnvironment(storageData.environment);
            }
        }

        setJoined(true);
    }, [progress, room, lobbyId, triggered, socket]);

    useEffect(() => {
        const handlePointerLockChange = () => {
            if (document.pointerLockElement === null && inPointerLock) {
                setInPointerLock(false);
                setMenu(true);
            }
        };

        document.addEventListener("pointerlockchange", handlePointerLockChange);

        return () => {
            document.removeEventListener("pointerlockchange", handlePointerLockChange);
        };
    }, [inPointerLock]);

    useEffect(() => {
        if (progress !== 100 || !room || hasStartedRef.current) {
            return;
        }
        hasStartedRef.current = true;

        const startSession = async () => {
            const isMultiplayer = !!lobbyId;
            const stored = JSON.parse(localStorage.getItem(room.url)) || {};
            const currentSessionId = stored?.sessionId ?? crypto.randomUUID();

            localStorage.setItem(
                room.url,
                JSON.stringify({
                    ...stored,
                    sessionId: currentSessionId,
                })
            );
            sessionIdRef.current = currentSessionId;

            try {
                await fetch("/api/ratings", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        sessionId: currentSessionId,
                        lobbyId: isMultiplayer ? lobbyId : null,
                        room: room.url,
                        user: session ? "User" : "Gast",
                        startedAt: Date.now(),
                    }),
                });
            } catch (error) {
                console.error("Fehler beim Senden des Session-Starts:", error);
            }
        };

        startSession();
    }, [progress, room, lobbyId, session]);

    // Game Session Lifecycle: Leave
    useEffect(() => {
        const handleExit = () => {
            const sessionId = sessionIdRef.current;
            if (!sessionId) {
                return;
            }

            const payload = JSON.stringify({
                sessionId,
                leftAt: Date.now(),
            });

            navigator.sendBeacon("/api/ratings", new Blob([payload], { type: "application/json" }));
        };

        window.addEventListener("beforeunload", handleExit);

        return () => {
            window.removeEventListener("beforeunload", handleExit);
        };
    }, []);

    if (!room) {
        return null;
    }

    const hasAccess = room?.price === 0 || user?.escaperooms.includes(room?.id) || lobbyId;

    return hasAccess ? (
        <div className="escape-room-screen">
            <KeyboardControls
                map={[
                    { name: "forward", keys: ["KeyW", "ArrowUp"] },
                    { name: "backward", keys: ["KeyS", "ArrowDown"] },
                    { name: "left", keys: ["KeyA", "ArrowLeft"] },
                    { name: "right", keys: ["KeyD", "ArrowRight"] },
                    { name: "run", keys: ["Shift"] },
                    { name: "jump", keys: ["Space"] },
                    { name: "inspect", keys: ["KeyF", "KeyE", "KeyI", "Enter"] },
                    { name: "menu", keys: ["KeyM", "Tab"] },
                    { name: "drop", keys: ["KeyQ"] },
                    { name: "zoomIn", keys: ["KeyZ", "KeyY", "KeyC"] },
                    { name: "escape", keys: ["Escape"] },
                    { name: "item1", keys: ["Digit1"] },
                    { name: "item2", keys: ["Digit2"] },
                    { name: "item3", keys: ["Digit3"] },
                    { name: "item4", keys: ["Digit4"] },
                    { name: "item5", keys: ["Digit5"] },
                    { name: "item6", keys: ["Digit6"] },
                    { name: "item7", keys: ["Digit7"] },
                    { name: "item8", keys: ["Digit8"] },
                    { name: "item9", keys: ["Digit9"] },
                ]}>
                <Canvas className="item-display" style={{ position: "absolute", pointerEvents: inspectedItem ? "auto" : "none" }}>
                    <InspectItem />
                    <Lights />
                </Canvas>

                <Canvas
                    key={room.url}
                    shadows
                    camera={{
                        position: room?.cameraPosition ?? [0, 0, 0],
                        rotation: room?.cameraRotation ?? [0, 0, 0],
                        fov: 50,
                        near: 0.1,
                        far: 1000,
                    }}
                    style={{ backgroundColor: room.backgroundColor ?? "black", pointerEvents: inspectedItem ? "none" : "auto" }}>
                    <Suspense>
                        <EscapeRoomComponent />
                    </Suspense>
                </Canvas>
                {progress < 100 && (
                    <h1 className="loading-display">
                        {progress.toFixed(0)}
                        {t("yourRooms.progress")}
                    </h1>
                )}
                {progress === 100 && (
                    <>
                        <Interface />
                        <Menu escaperoomHints={room?.hints} roomUrl={room?.url} title={room?.title} />
                        <Sounds />
                        <EndScreen />
                    </>
                )}
            </KeyboardControls>
        </div>
    ) : (
        <Section variant="purple-pink" className="rooms-section">
            <Title order={2} align="center" mb="md" c="#c9c9c9">
                {user}
                {user ? t("yourRooms.noRoom") : t("yourRooms.noLoginEscapeRoom")}
            </Title>
            <Text mt="md" color="#c9c9c9" align="center" mb="md">
                {user ? t("yourRooms.noRoomDescription") : t("yourRooms.noLoginDescription")}
            </Text>

            <Button className="donation-button" component="a" href="/" radius="xl" size="lg">
                {t("details.back")}
            </Button>
        </Section>
    );
};

export default EscapeRoom;
